import { createContext, useEffect, useMemo } from "react";
import { Outlet, useParams } from "react-router-dom";
import ProductDetails from "@/features/product-details/ProductDetails";
import { useFetchMenusQuery } from "../organizations/api/menusApi";
import { useAppDispatch, useOrder } from "@/hooks";
import { fetchCategories } from "../categories/slice";
import useOrganizations, { OrganizationFetchConfig } from "../organizations/useOrganizations";
import { Menu, Order, OrderStatus, Organization, OrgCategory } from "@/types";
import { MENU_FLOWS } from "@/constants/global";
import { LocalStorage } from "@/constants/localStorage";
import { useGetOrderByIdQuery } from "../organizations/api/ordersApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { fetchUsers } from "@/store/usersSlice";
import { setOrdersFlow, updateOrder } from "../order/slice";
import { useFetchBranchQuery } from "../organizations/api/branchApi";
import { Branch } from "@/types/branch";
import OrganizationBranchesDrawer from "@/pages/organization-details/components/OrganizationBranchesDrawer/OrganizationBranchesDrawer";

export interface UserOrganizationContextType extends Partial<Organization> {
  menus?: Menu[];
  orders?: Order[];
  categories?: OrgCategory[];
  branches?: Branch[];
}

const initialState = {
  content: undefined,
  menus: undefined,
  tables: undefined,
  orders: undefined,
  categories: undefined,
  branches: undefined,
};

export const UserOrganizationContext = createContext<UserOrganizationContextType>(initialState);

interface Props extends OrganizationFetchConfig {
  showOrderDetails?: boolean;
  showContactForm?: boolean;
  showBranchDetails?: boolean;
}

const UserOrganizationProvider = ({ showOrderDetails = true, showBranchDetails = true, ...rest }: Props) => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const dispatch = useAppDispatch();
  const menuQuery = useFetchMenusQuery(organizationId);
  const branchesQuery = useFetchBranchQuery(organizationId || skipToken, {
    skip: !organizationId,
  });
  const { currentOrganization, getOrgDetails } = useOrganizations();
  const { getOrderInLocalStorage, deleteLSOrganizationOrder } = useOrder();

  const orderId = useMemo(() => {
    if (!organizationId) return null;
    return getOrderInLocalStorage(organizationId);
  }, [getOrderInLocalStorage, organizationId]);

  const { data, isLoading, isFetching } = useGetOrderByIdQuery(
    {
      organizationId: organizationId ?? skipToken,
      orderId: orderId ?? skipToken,
    },
    {
      skip: !organizationId || !orderId,
    },
  );

  useEffect(() => {
    if (organizationId) {
      getOrgDetails(rest);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // Check if the dine mode is stored in the local storage
  useEffect(() => {
    enum DineModes {
      dinein = MENU_FLOWS.DINE_IN,
    }

    const qs = new URLSearchParams(window.location.search);
    const dineMode = (qs.get("diningmode") || localStorage.getItem(LocalStorage.DINING_MODE)) as DineModes & MENU_FLOWS;

    if (Object.keys(DineModes).includes(String(dineMode))) {
      dispatch(setOrdersFlow(DineModes[dineMode]));
      return;
    }

    if (Object.values(MENU_FLOWS).includes(dineMode)) {
      dispatch(setOrdersFlow(dineMode));
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLoading || isFetching) return; // still loading or fetching
    if (!data) return; // no data don't do anything

    if (data.status === OrderStatus.DRAFT) {
      if (data.createdBy) {
        dispatch(fetchUsers([data.createdBy]));
      }
      dispatch(updateOrder(data));
    } else {
      deleteLSOrganizationOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  if (menuQuery.isLoading || branchesQuery.isLoading) {
    return null;
  }

  return (
    <UserOrganizationContext.Provider
      value={{
        content: currentOrganization?.content ?? [],
        tables: currentOrganization?.tables ?? [],
        branches: branchesQuery?.data ?? [],
        orders: currentOrganization?.orders ?? [],
        categories: currentOrganization?.categories ?? [],
        menus: menuQuery?.data,
      }}
    >
      <Outlet />

      {showOrderDetails ? <ProductDetails /> : null}
      {showBranchDetails ? <OrganizationBranchesDrawer /> : null}
    </UserOrganizationContext.Provider>
  );
};

export default UserOrganizationProvider;
